import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { DeployTest } from '../../types';
import { API_BASE_URL } from '../../config/api';
import { BETA_PROGRAM, INTERNAL_PROGRAM } from '../../config/deployProgram';
import { isTestingHost } from '../../config/hosts';
import {
  firmwareTypeLabel,
  formatQuestionAnswer,
  releaseNoteItems,
  resolveItemStatus,
  statusMeta,
} from './deployTestUtils';

const formatDate = (value?: string | Date) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const formatPerson = (test: DeployTest) => {
  const person = test.createdBy;
  if (!person) return '—';
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
  return name || '—';
};

const taskStatusLabel = (test: DeployTest, item: DeployTest['items'][number]) => {
  if (test.status === 'planning') return 'Planning';
  if (test.status === 'aborted') return 'Aborted';
  const status = resolveItemStatus(item);
  if (status === 'passed') return 'Passed';
  if (status === 'flagged') return 'Flagged';
  if (status === 'failed') return 'Failed';
  return 'Testing';
};

const PublicDeployTestPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const program = isTestingHost() ? INTERNAL_PROGRAM : BETA_PROGRAM;
  const [test, setTest] = useState<DeployTest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!name) {
        setError('Test not found');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}${program.testsApi}/public/${encodeURIComponent(name)}`
        );
        if (!cancelled) {
          setTest(response.data.test);
          setError(null);
          if (response.data.test?.name) {
            document.title = `${program.brand} - ${response.data.test.name}`;
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setTest(null);
          setError(err.response?.data?.message || 'Deploy test not found');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [name, program.brand, program.testsApi]);

  const items = test?.items || [];
  const notes = releaseNoteItems(test?.releaseNotes);
  const passedCount = items.filter((item) => resolveItemStatus(item) === 'passed').length;
  const firmwareLabel = firmwareTypeLabel(test?.firmwareType);
  const mobileAppPlatforms = test?.mobileAppPlatforms?.length ? test.mobileAppPlatforms : ['ios', 'android'];

  return (
    <div className={`beta-portal${program.id === 'internal' ? ' beta-portal-internal' : ''}`}>
      <header className="beta-portal-topbar">
        <Link to="/" className="beta-portal-brand">
          <img src="/logo-mark.png" alt="" className="beta-portal-mark" />
          <div className="beta-portal-kicker">{program.brand}</div>
        </Link>
      </header>
    <main className="public-test-page">
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error || !test ? (
        <div className="public-test-doc">
          <div className="public-test-kicker">{program.takeKicker}</div>
          <h1>Test not found</h1>
          <p className="text-muted mb-0">{error || 'This deploy test does not exist.'}</p>
        </div>
      ) : (
        <article className="public-test-doc">
          <div className="public-test-kicker">{program.takeKicker}</div>
          <h1>{test.name}</h1>

          <div className="public-test-versions">
            <div>
              <div className="public-test-version-label">Firmware</div>
              <div className={`public-test-version-value${test.firmwareType ? ` is-${test.firmwareType}` : ''}`}>
                {test.firmwareVersion ? `v${test.firmwareVersion}` : 'Not selected'}
                {firmwareLabel && (
                  <span className={`deploy-test-fw-badge is-${test.firmwareType}`}>{firmwareLabel}</span>
                )}
              </div>
            </div>
            {mobileAppPlatforms.includes('ios') && (
              <div>
                <div className="public-test-version-label">Mobile App (iOS)</div>
                <div className="public-test-version-value">
                  {test.mobileAppVersionIos || test.mobileAppVersion ? `v${test.mobileAppVersionIos || test.mobileAppVersion}` : 'Not selected'}
                </div>
              </div>
            )}
            {mobileAppPlatforms.includes('android') && (
              <div>
                <div className="public-test-version-label">Mobile App (Android)</div>
                <div className="public-test-version-value">
                  {test.mobileAppVersionAndroid || test.mobileAppVersion ? `v${test.mobileAppVersionAndroid || test.mobileAppVersion}` : 'Not selected'}
                </div>
              </div>
            )}
          </div>

          <div className={`public-test-status is-${test.status}`}>
            {statusMeta(test.status).label}
          </div>

          {test.overview?.trim() && (
            <section className="public-test-section">
              <h2>Instructions</h2>
              <pre className="public-test-body">{test.overview}</pre>
            </section>
          )}

          <dl className="public-test-meta">
            <div>
              <dt>Created</dt>
              <dd>{formatDate(test.createdAt)}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{formatDate(test.updatedAt)}</dd>
            </div>
            <div>
              <dt>Created by</dt>
              <dd>{formatPerson(test)}</dd>
            </div>
          </dl>

          <section className="public-test-section">
            <h2>Release Notes</h2>
            {notes.length === 0 ? (
              <p className="text-muted mb-0">None</p>
            ) : (
              <ul className="public-test-notes">
                {notes.map((note, index) => (
                  <li key={`${test._id}-note-${index}`}>{note}</li>
                ))}
              </ul>
            )}
          </section>

          <section className="public-test-section">
            <h2>Tasks ({passedCount} of {items.length} complete)</h2>
            {items.length === 0 ? (
              <p className="text-muted mb-0">No tasks yet</p>
            ) : (
              <ul className="public-test-tasks">
                {items.map((item, index) => {
                  const itemStatus = taskStatusLabel(test, item);
                  return (
                    <li key={item._id || `${test._id}-${index}`}>
                      <div className="public-test-task-top">
                        <strong>{index + 1}. {item.text}</strong>
                        <span className={`deploy-test-card-task-status is-${itemStatus.toLowerCase()}`}>
                          {itemStatus}
                        </span>
                      </div>
                      {item.description?.trim() && (
                        <p>{item.description}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="public-test-section">
            <h2>Questions</h2>
            {(test.questions || []).length === 0 ? (
              <p className="text-muted mb-0">No questions yet</p>
            ) : (
              <ul className="public-test-tasks">
                {(test.questions || []).map((question, index) => (
                  <li key={question._id || `${test._id}-q-${index}`}>
                    <div className="public-test-task-top">
                      <strong>{index + 1}. {question.prompt}</strong>
                      <span className="deploy-test-card-task-status is-planning">
                        {question.type === 'boolean' ? 'Yes / No' : question.type === 'choice' ? 'Multiple choice' : 'Text'}
                      </span>
                    </div>
                    {question.type === 'choice' && (question.choices || []).filter(Boolean).length > 0 && (
                      <p>Choices: {(question.choices || []).filter(Boolean).join(', ')}</p>
                    )}
                    {question.type !== 'text' && question.additionalInfo?.trim() && (
                      <p>{question.additionalInfo}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="public-test-section">
            <h2>Kickoff Email</h2>
            <pre className="public-test-body">{test.kickoffEmail?.trim() || 'None'}</pre>
          </section>

          {(test.status === 'succeeded' || test.status === 'failed') && (
            <section className="public-test-section">
              <h2>Answers & Feedback</h2>
              {(test.results || []).length === 0 ? (
                <p className="text-muted mb-0">No completed tester responses yet</p>
              ) : (
                <ul className="public-test-results">
                  {(test.results || []).map((result, index) => (
                    <li key={`${result.tester}-${result.phone}-${index}`}>
                      <div className="public-test-result-who">
                        <strong>{result.tester}</strong>
                        <span>{result.phone}</span>
                      </div>
                      {result.taskResponses?.length ? (
                        <ul className="public-test-result-tasks">
                          {result.taskResponses.map((task, taskIndex) => (
                            <li key={`${index}-task-${taskIndex}`} className={task.done ? 'is-done' : 'is-open'}>
                              {task.done ? 'Done' : 'Open'}: {task.text}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {result.questionResponses?.length ? (
                        <div className="public-test-result-answers">
                          {result.questionResponses.map((answer, answerIndex) => (
                            <div key={`${index}-answer-${answerIndex}`}>
                              <div className="public-test-result-prompt">{answer.prompt}</div>
                              <div>{formatQuestionAnswer(answer.value, answer.type)}</div>
                              {answer.additionalInfo?.trim() ? (
                                <p>{answer.additionalInfo}</p>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </article>
      )}
    </main>
    </div>
  );
};

export default PublicDeployTestPage;
